import { useEnvSettings } from '@e2m-customer-portal/hooks';
import { Button, IconButton } from '@mui/material';
import clsx from 'clsx';
import { OUTDATED_BOOKMARKS_KEY } from 'core/sidebar/Sidebar';
import {
  addDays,
  isAfter,
  isFuture,
  isPast,
  isWednesday,
  parseISO,
  previousWednesday,
  startOfDay,
  subDays,
} from 'date-fns';
import { ReactElement, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CancelIcon } from '../../assets/icons';

const MAINTENANCE_STORAGE_KEY = 'acknowledged-maintenance-window';

interface MaintenanceBannerProps {
  onHeightChange: React.Dispatch<React.SetStateAction<number>>;
}

export function MaintenanceBanner({
  onHeightChange,
}: Readonly<MaintenanceBannerProps>): ReactElement<MaintenanceBannerProps> {
  const [t] = useTranslation();
  const userHasOutdatedBookmarks = !!localStorage.getItem(OUTDATED_BOOKMARKS_KEY);

  const [bannerRef, setBannerRef] = useState<HTMLDivElement | null>();
  useEffect(() => {
    const updateHeight = () => {
      if (bannerRef) onHeightChange(bannerRef.offsetHeight);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [userHasOutdatedBookmarks, bannerRef, onHeightChange]);

  const { maintenanceWindowStart, maintenanceWindowEnd } = useEnvSettings();
  const start = parseISO(maintenanceWindowStart);
  const actualEnd = parseISO(maintenanceWindowEnd);
  const maintenanceIsComing = !Number.isNaN(actualEnd.getTime()) && !isAfter(start, actualEnd) && !isPast(actualEnd);

  // If there’s no maintenance planned, there might still be the day of repentance and prayer coming up
  const lastPossibleRepentAndPrayDay = parseISO(`${new Date().getFullYear()}-11-21T23:00:00.000Z`);
  const repentAndPrayDay = isWednesday(lastPossibleRepentAndPrayDay)
    ? lastPossibleRepentAndPrayDay
    : previousWednesday(lastPossibleRepentAndPrayDay);
  const repentAndPrayStart = subDays(repentAndPrayDay, 7);
  const repentAndPrayEnd = addDays(repentAndPrayDay, 1);
  const isRepentAndPrayTime = !maintenanceIsComing && isPast(repentAndPrayStart) && isFuture(repentAndPrayEnd);

  const hideSelf = isRepentAndPrayTime
    ? localStorage.getItem(MAINTENANCE_STORAGE_KEY) === `day-of-repentance-and-prayer-${new Date().getFullYear()}`
    : localStorage.getItem(MAINTENANCE_STORAGE_KEY) === `${maintenanceWindowStart}-${maintenanceWindowEnd}`;

  const [exitTransitionStarted, setExitTransitionStarted] = useState(false);

  if (!userHasOutdatedBookmarks && (hideSelf || (!maintenanceIsComing && !isRepentAndPrayTime))) return <></>;

  // If maintenance ends at midnight, fake it to
  // make it match 24 o'clock of the previous day
  const end = actualEnd.getTime() === startOfDay(actualEnd).getTime() ? subDays(actualEnd, 1) : actualEnd;

  const spansMultipleDays = start.getDate() !== end.getDate();

  const closeBanner = () => {
    setExitTransitionStarted(true);
    if (bannerRef) onHeightChange(0);
    setTimeout(() => {
      if (userHasOutdatedBookmarks) {
        localStorage.removeItem(OUTDATED_BOOKMARKS_KEY);
      } else {
        localStorage.setItem(
          MAINTENANCE_STORAGE_KEY,
          isRepentAndPrayTime
            ? `day-of-repentance-and-prayer-${new Date().getFullYear()}`
            : `${maintenanceWindowStart}-${maintenanceWindowEnd}`,
        );
      }
      setExitTransitionStarted(false);
    }, 600);
  };

  return (
    <div
      ref={setBannerRef}
      className={clsx(
        'z-10 flex w-full origin-top flex-col items-center bg-green-200/50 transition-all duration-300',
        exitTransitionStarted ? 'absolute scale-y-0 opacity-0' : 'relative',
      )}
    >
      {!exitTransitionStarted && (
        <div className="absolute right-2 top-2">
          <IconButton title={t('SHARED_OK')} className="p-0 text-green-300" onClick={closeBanner}>
            <CancelIcon />
          </IconButton>
        </div>
      )}
      <div className="m-4">
        {userHasOutdatedBookmarks || isRepentAndPrayTime ? (
          <p className="m-0 mr-4">
            <Trans
              components={{
                code: <code className="bg-black/10 leading-loose p-1 rounded-md shadow-inner" />,
                div: <div className="my-2 text-center" />,
              }}
              values={{ address: location.href, day: repentAndPrayDay }}
            >
              {userHasOutdatedBookmarks ? 'BANNER_OUTDATED_BOOKMARKS' : 'BANNER_DAY_OF_REPENTANCE_AND_PRAYER_TEXT'}
            </Trans>
            {userHasOutdatedBookmarks && (
              <div className="text-center">
                <Button onClick={closeBanner}>{t('SHARED_ACKNOWLEDGED')}</Button>
              </div>
            )}
          </p>
        ) : (
          <>
            <h2 className="m-0 mr-4">
              {spansMultipleDays
                ? t('BANNER_MAINTENANCE_TITLE_MULTIPLE', {
                    start,
                    end,
                  })
                : t('BANNER_MAINTENANCE_TITLE', { start })}
            </h2>
            <p className="m-0">
              {spansMultipleDays
                ? t('BANNER_MAINTENANCE_TEXT_MULTIPLE', { start, end })
                : t('BANNER_MAINTENANCE_TEXT', { start, end })}
            </p>
          </>
        )}
      </div>
      <div className="h-2 w-full [background-image:repeating-linear-gradient(135deg,#000,#000_10px,#4f9e30ff_10px,#4f9e30ff_20px)]" />
    </div>
  );
}
